import React, { useState } from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';
import { combineSchemas, organizationSchema, localBusinessSchema, breadcrumbSchema } from '../lib/schema';
import { useToast } from '../hooks/use-toast';
import { submitLead } from '../api';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';

const services = ['General Inquiry', 'Web Design', 'eCommerce', 'A.I. Integration', 'Marketing', 'Hosting', 'Other'];

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', service: 'General Inquiry' });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Missing info', description: 'Name and email are required.' });
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, source: 'Contact Page' });
      toast({ title: 'Message Sent!', description: `Thanks ${form.name}. We\u2019ll be in touch within 24 hours.` });
      setForm({ name: '', email: '', phone: '', message: '', service: 'General Inquiry' });
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Contact Website Symphony | Get a Free Web Design Quote"
        description="Contact Website Symphony for a free, no-obligation web design and marketing quote. Call (305) 812-5999, email Info@WebsiteSymphony.com or fill out our contact form."
        canonical="/contact"
        schema={combineSchemas(organizationSchema, localBusinessSchema, breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]))}
      />
      <PageHero title="Contact Us" subtitle="Tell us a little about your project and we'll get back to you with a free, no-obligation estimate." breadcrumb="Contact" />

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1 space-y-6">
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-[#2C5F66]/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="text-[#2C5F66]" size={20} />
              </span>
              <div>
                <h4 className="text-[#2C5F66] font-bold mb-1">Our Office</h4>
                <p className="text-gray-600 text-sm">420 NE 67th St, Suite 711-North<br />Miami Beach, FL 33141</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-[#2C5F66]/10 flex items-center justify-center flex-shrink-0">
                <Phone className="text-[#2C5F66]" size={20} />
              </span>
              <div>
                <h4 className="text-[#2C5F66] font-bold mb-1">Call Us</h4>
                <a href="tel:+13058125999" className="text-gray-600 text-sm hover:text-[#D9A04A]">(305) 812-5999</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-[#2C5F66]/10 flex items-center justify-center flex-shrink-0">
                <Mail className="text-[#2C5F66]" size={20} />
              </span>
              <div>
                <h4 className="text-[#2C5F66] font-bold mb-1">Email Us</h4>
                <a href="mailto:Info@WebsiteSymphony.com" className="text-gray-600 text-sm hover:text-[#D9A04A]">Info@WebsiteSymphony.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-[#2C5F66]/10 flex items-center justify-center flex-shrink-0">
                <Clock className="text-[#2C5F66]" size={20} />
              </span>
              <div>
                <h4 className="text-[#2C5F66] font-bold mb-1">Business Hours</h4>
                <p className="text-gray-600 text-sm">Mon – Fri: 9:00 AM – 6:00 PM<br />Sat – Sun: By Appointment</p>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="md:col-span-2 bg-[#F7F7F5] rounded-xl p-8 md:p-10 grid md:grid-cols-2 gap-5">
            <input name="name" value={form.name} onChange={onChange} placeholder="Full Name *" className="bg-white border border-gray-200 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#2C5F66]" />
            <input name="email" type="email" value={form.email} onChange={onChange} placeholder="Email Address *" className="bg-white border border-gray-200 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#2C5F66]" />
            <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone Number" className="bg-white border border-gray-200 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#2C5F66]" />
            <select name="service" value={form.service} onChange={onChange} className="bg-white border border-gray-200 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#2C5F66] text-gray-700">
              {services.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <textarea name="message" value={form.message} onChange={onChange} rows={6} placeholder="Tell us about your project…" className="md:col-span-2 bg-white border border-gray-200 rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#2C5F66] resize-none" />
            <button type="submit" disabled={loading} className="md:col-span-2 bg-[#C68B30] hover:bg-[#B17A24] disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-md transition-colors">
              {loading ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
